import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Plus, Trash2, Loader2, HandCoins, FileDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { PrepaToggle } from './PrepaToggle';

const TYPES = { tests_selection: 'Tests de sélection', journee_preparation: 'Journée de préparation', autre: 'Autre' };
const MODES = { especes: 'Espèces', cheque: 'Chèque', virement: 'Virement', cb_sur_place: 'CB sur place' };
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const emptyForm = () => ({ type: 'tests_selection', libelle: '', montant: '', date: today(), mode: 'virement', reference: '' });
const selectCls = 'w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#8DC63F]';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');
const fmtMontant = (m) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(m || 0);
export const paiementLabel = (p) => (p.type === 'autre' ? (p.libelle || 'Autre') : (TYPES[p.type] || p.type));

export const downloadRecu = async (url, p) => {
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    const disposition = response.headers?.['content-disposition'] || '';
    const name = (disposition.match(/filename="?([^";]+)"?/) || [])[1] || `Recu_${p.numero_recu || 'paiement'}.pdf`;
    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (e) {
    toast.error('Impossible de générer le reçu');
  }
};

const PaiementManuelDialog = ({ open, onOpenChange, candidatId, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { if (open) setForm(emptyForm()); }, [open]);

  const submit = async () => {
    if (!form.montant || Number(form.montant) <= 0) return toast.error('Indiquez un montant');
    if (form.type === 'autre' && !form.libelle.trim()) return toast.error("Précisez l'objet du paiement");
    setSaving(true);
    try {
      const { data } = await axios.post(`/candidats/${candidatId}/paiements-manuels`, { ...form, montant: Number(form.montant) });
      toast.success('Paiement enregistré');
      onSaved(data.paiements);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="paiement-manuel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HandCoins className="h-5 w-5 text-[#8DC63F]" /> Paiement manuel</DialogTitle>
          <DialogDescription>Enregistrez un paiement reçu en dehors de l'application.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Objet du paiement</Label>
            <select value={form.type} onChange={set('type')} className={selectCls} data-testid="paiement-manuel-type">
              {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {form.type === 'autre' && (
            <div>
              <Label>Précisez</Label>
              <Input value={form.libelle} onChange={set('libelle')} placeholder="Ex. : frais de dossier" data-testid="paiement-manuel-libelle" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Montant (€)</Label>
              <Input type="number" min="0" step="0.01" value={form.montant} onChange={set('montant')} placeholder="0,00" data-testid="paiement-manuel-montant" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={set('date')} data-testid="paiement-manuel-date" />
            </div>
          </div>
          <div>
            <Label>Mode de paiement</Label>
            <select value={form.mode} onChange={set('mode')} className={selectCls} data-testid="paiement-manuel-mode">
              {Object.entries(MODES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <Label>Référence / commentaire (facultatif)</Label>
            <Input value={form.reference} onChange={set('reference')} placeholder="N° de chèque, référence du virement…" data-testid="paiement-manuel-reference" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="paiement-manuel-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#8DC63F] hover:bg-[#7AB62F]" data-testid="paiement-manuel-submit">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PaiementRow = ({ p, onDelete, deleting, onRecu, recuLoading }) => (
  <div className="flex justify-between items-center gap-3 p-3 rounded-lg bg-green-50" data-testid={`paiement-row-${p.transaction_id || p.session_id}`}>
    <div className="min-w-0">
      <p className="font-medium text-sm flex items-center gap-2 flex-wrap">
        {paiementLabel(p)}
        {p.source === 'manuel' && <Badge variant="outline" className="text-[10px] py-0 border-slate-300 text-slate-600">Manuel</Badge>}
      </p>
      <p className="text-xs text-slate-500">
        {fmtDate(p.date)}{p.mode_label ? ` · ${p.mode_label}` : ''}{p.reference ? ` · ${p.reference}` : ''}
      </p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <div className="text-right">
        <p className="font-bold">{fmtMontant(p.montant)}</p>
        <Badge className="bg-green-100 text-green-700 text-xs">Payé</Badge>
      </div>
      {p.transaction_id && (
        <button onClick={() => onRecu(p)} disabled={recuLoading} className="p-1.5 rounded-md text-slate-500 hover:text-[#5d8a22] hover:bg-green-100" title="Télécharger le reçu" data-testid={`paiement-recu-${p.transaction_id}`}>
          {recuLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
        </button>
      )}
      {p.source === 'manuel' && (
        <button onClick={() => onDelete(p)} disabled={deleting} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50" title="Supprimer" data-testid={`paiement-delete-${p.transaction_id}`}>
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </button>
      )}
    </div>
  </div>
);

export const PaiementsCandidatCard = ({ candidatId, paiements, onChange, faitPrepa }) => {
  const [open, setOpen] = useState(false);
  const prepaPayee = paiements.some(p => p.type === 'journee_preparation' && p.statut === 'completed');
  const [faitPrepaLocal, setFaitPrepaLocal] = useState(!!faitPrepa);
  useEffect(() => { setFaitPrepaLocal(!!faitPrepa); }, [faitPrepa]);
  // Un paiement de prépa enregistré inscrit le candidat côté serveur : on garde l'état aligné
  useEffect(() => { if (prepaPayee) setFaitPrepaLocal(true); }, [prepaPayee]);
  const [deletingId, setDeletingId] = useState(null);
  const [recuId, setRecuId] = useState(null);

  const recu = async (p) => {
    setRecuId(p.transaction_id);
    await downloadRecu(`/candidats/${candidatId}/paiements/${p.transaction_id}/recu`, p);
    setRecuId(null);
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le paiement « ${paiementLabel(p)} » de ${fmtMontant(p.montant)} ?`)) return;
    setDeletingId(p.transaction_id);
    try {
      const { data } = await axios.delete(`/candidats/${candidatId}/paiements-manuels/${p.transaction_id}`);
      onChange(data.paiements);
      toast.success('Paiement supprimé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card data-testid="paiements-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CreditCard className="h-5 w-5 text-[#8DC63F]" /> Paiements
        </CardTitle>
        <div className="flex items-center gap-4">
          <PrepaToggle candidatId={candidatId} value={faitPrepaLocal} payee={prepaPayee} onChange={setFaitPrepaLocal} />
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="border-[#8DC63F] text-[#5d8a22] hover:bg-green-50" data-testid="btn-paiement-manuel">
            <Plus className="h-4 w-4 mr-1" /> Paiement manuel
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {paiements.length > 0 ? (
          paiements.map((p, i) => (
            <PaiementRow key={p.transaction_id || i} p={p} onDelete={remove} deleting={deletingId === p.transaction_id}
              onRecu={recu} recuLoading={recuId === p.transaction_id} />
          ))
        ) : (
          <p className="text-slate-500 text-sm italic" data-testid="paiements-empty">Aucun paiement enregistré</p>
        )}
      </CardContent>
      <PaiementManuelDialog open={open} onOpenChange={setOpen} candidatId={candidatId} onSaved={onChange} />
    </Card>
  );
};
